/**
 * Hot Folders React Query Hooks
 *
 * TanStack Query hooks for hot folder management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateHotFolderData,
  UpdateHotFolderData,
  HotFolderFileSearchFilters,
  UpdateOverrideData,
  FolderType,
} from '@/domain/hot-folder/types'
import { useAuth } from '@/application/auth'
import * as hotFolderService from './hotFolder.service'

// =============================================================================
// Query Keys
// =============================================================================

export const hotFolderKeys = {
  all: ['hot-folders'] as const,
  lists: () => [...hotFolderKeys.all, 'list'] as const,
  list: () => [...hotFolderKeys.lists()] as const,
  details: () => [...hotFolderKeys.all, 'detail'] as const,
  detail: (id: string) => [...hotFolderKeys.details(), id] as const,
  files: (id: string) => [...hotFolderKeys.all, 'files', id] as const,
  filesByFilter: (id: string, filters: HotFolderFileSearchFilters) =>
    [...hotFolderKeys.files(id), filters] as const,
  stats: (id: string) => [...hotFolderKeys.all, 'stats', id] as const,
  scheduler: () => [...hotFolderKeys.all, 'scheduler'] as const,
}

// =============================================================================
// Hot Folder Query Hooks
// =============================================================================

/**
 * Hook to fetch list of hot folders
 */
export function useHotFolders() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: hotFolderKeys.list(),
    queryFn: () => hotFolderService.getHotFolders(),
    enabled: isAuthenticated,
  })
}

/**
 * Hook to fetch a single hot folder by ID
 */
export function useHotFolder(id: string | undefined) {
  return useQuery({
    queryKey: hotFolderKeys.detail(id ?? ''),
    queryFn: () => hotFolderService.getHotFolderById(id!),
    enabled: !!id,
  })
}

/**
 * Hook to fetch hot folder files
 */
export function useHotFolderFiles(
  hotFolderId: string | undefined,
  filters?: Omit<HotFolderFileSearchFilters, 'hotFolderId'>
) {
  return useQuery({
    queryKey: hotFolderKeys.filesByFilter(hotFolderId ?? '', filters ?? {}),
    queryFn: () => hotFolderService.getHotFolderFiles(hotFolderId!, filters),
    enabled: !!hotFolderId,
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

/**
 * Hook to fetch hot folder statistics
 */
export function useHotFolderStats(hotFolderId: string | undefined) {
  return useQuery({
    queryKey: hotFolderKeys.stats(hotFolderId ?? ''),
    queryFn: () => hotFolderService.getHotFolderStats(hotFolderId!),
    enabled: !!hotFolderId,
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

/**
 * Hook to fetch scheduler status
 */
export function useSchedulerStatus() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: hotFolderKeys.scheduler(),
    queryFn: () => hotFolderService.getSchedulerStatus(),
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refresh every minute
  })
}

// =============================================================================
// Hot Folder Mutation Hooks
// =============================================================================

/**
 * Hook to create a new hot folder
 */
export function useCreateHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateHotFolderData) => hotFolderService.createHotFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.lists() })
    },
  })
}

/**
 * Hook to update a hot folder
 */
export function useUpdateHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHotFolderData }) =>
      hotFolderService.updateHotFolder(id, data),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(hotFolderKeys.detail(variables.id), result)
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.lists() })
    },
  })
}

/**
 * Hook to delete a hot folder
 */
export function useDeleteHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => hotFolderService.deleteHotFolder(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: hotFolderKeys.detail(deletedId) })
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.lists() })
    },
  })
}

/**
 * Hook to toggle hot folder enabled/disabled
 */
export function useToggleHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => hotFolderService.toggleHotFolder(id),
    onSuccess: (result, id) => {
      queryClient.setQueryData(hotFolderKeys.detail(id), result)
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.lists() })
    },
  })
}

/**
 * Hook to test connection to a folder
 */
export function useTestConnection() {
  return useMutation({
    mutationFn: ({
      folderPath,
      folderType,
      credentials,
    }: {
      folderPath: string
      folderType: FolderType
      credentials?: {
        networkUsername?: string
        networkPassword?: string
        networkDomain?: string
      }
    }) => hotFolderService.testConnection(folderPath, folderType, credentials),
  })
}

/**
 * Hook to trigger manual scan
 */
export function useScanHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (hotFolderId: string) => hotFolderService.scanHotFolder(hotFolderId),
    onSuccess: (_, hotFolderId) => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.files(hotFolderId) })
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.stats(hotFolderId) })
    },
  })
}

/**
 * Hook to reset hot folder files
 * - Without deleteAll: resets COMPLETED/FAILED files back to DETECTED for reprocessing
 * - With deleteAll=true: deletes all file records (requires re-scan)
 */
export function useResetHotFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ hotFolderId, deleteAll = false }: { hotFolderId: string; deleteAll?: boolean }) =>
      hotFolderService.resetHotFolder(hotFolderId, deleteAll),
    onSuccess: (_, { hotFolderId }) => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.files(hotFolderId) })
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.stats(hotFolderId) })
    },
  })
}

// =============================================================================
// File Processing Mutation Hooks
// =============================================================================

/**
 * Hook to process a single file
 */
export function useProcessFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => hotFolderService.processFile(fileId),
    onSuccess: () => {
      // Invalidate all file queries since we don't know which folder
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.all })
    },
  })
}

/**
 * Hook to process multiple files
 */
export function useProcessBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileIds: string[]) => hotFolderService.processBatch(fileIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.all })
    },
  })
}

/**
 * Hook to skip a file
 */
export function useSkipFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => hotFolderService.skipFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.all })
    },
  })
}

/**
 * Hook to update file override fields
 */
export function useUpdateOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fileId, data }: { fileId: string; data: UpdateOverrideData }) =>
      hotFolderService.updateOverride(fileId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotFolderKeys.all })
    },
  })
}
